package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.data.dto.finance.AdjustmentDto
import com.evoapps.evoshift.data.dto.finance.CostDto
import com.evoapps.evoshift.data.dto.finance.DailyRecordDto
import com.evoapps.evoshift.data.dto.finance.RosterFinancialsDto
import com.evoapps.evoshift.data.dto.finance.ShiftFinancialsDto
import com.evoapps.evoshift.data.dto.roster.ShiftDto
import com.evoapps.evoshift.data.mapper.AdjustmentMapper
import com.evoapps.evoshift.data.mapper.CostMapper
import com.evoapps.evoshift.data.mapper.DailyRecordMapper
import com.evoapps.evoshift.data.mapper.RosterFinancialsMapper
import com.evoapps.evoshift.data.mapper.ShiftFinancialsMapper
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DailyRevenue
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.RosterFinancials
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.util.WorkplaceAccessRevokedException
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map

class FinancialRepositoryImpl(
    private val firestore: AppFirestore,
    private val rosterFinancialsMapper: RosterFinancialsMapper,
    private val dailyRecordMapper: DailyRecordMapper,
    private val shiftFinancialsMapper: ShiftFinancialsMapper,
    private val costMapper: CostMapper,
    private val adjustmentMapper: AdjustmentMapper
) : FinancialRepository {

    // --- READ OPERATIONS (Bez zmian) ---

    override suspend fun getRosterFinancials(rosterId: String): Result<RosterFinancials> {
        return try {
            val snapshot = firestore.collection("roster_financials").document(rosterId).get()
            if (snapshot.exists) {
                val dto = snapshot.data<RosterFinancialsDto>()
                Result.success(rosterFinancialsMapper.toDomain(dto))
            } else {
                Result.failure(NotFoundException("Roster financials $rosterId"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateRosterFinancials(
        rosterId: String,
        financials: RosterFinancials
    ): Result<Unit> {
        return try {
            val dto = rosterFinancialsMapper.toDto(financials)
            firestore.collection("roster_financials").document(rosterId).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getDailyRecord(dailyRecordId: String): Result<DailyRecord> {
        return try {
            val snapshot = firestore.collection("daily_records").document(dailyRecordId).get()
            if (snapshot.exists) {
                val dto = snapshot.data<DailyRecordDto>()
                Result.success(dailyRecordMapper.toDomain(dto))
            } else {
                Result.failure(Exception("Daily Record not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getDailyRecordsForRoster(rosterId: String): Result<List<DailyRecord>> {
        return try {
            // firestore.rules check the record's workplace: a query must constrain it (SH-28).
            val snapshot = firestore.collection("daily_records")
                .where { "workplaceId" equalTo RosterIds.workplaceIdOf(rosterId) }
                .where { "rosterId" equalTo rosterId }
                .get()
            val records = snapshot.documents.map {
                dailyRecordMapper.toDomain(it.data<DailyRecordDto>())
            }
            Result.success(records)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getDailyRecordsForWorkplaceByDate(
        workplaceId: String,
        date: String
    ): Result<List<DailyRecord>> {
        return try {
            val snapshot = firestore.collection("daily_records")
                .where { "workplaceId" equalTo workplaceId }
                .where { "date" equalTo date }
                .get()
            val records = snapshot.documents.map {
                dailyRecordMapper.toDomain(it.data<DailyRecordDto>())
            }
            Result.success(records)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getDailyRecordsInRange(workplaceId: String, from: String, to: String): Result<List<DailyRecord>> = runCatching {
        // SH-225: firestore.rules check the record's workplace, so the query constrains it; the
        // dates are ISO strings, so their order is the days' (index: workplaceId, date).
        firestore.collection("daily_records")
            .where { "workplaceId" equalTo workplaceId }
            .where { "date" greaterThanOrEqualTo from }
            .where { "date" lessThanOrEqualTo to }
            .get()
            .documents.map { dailyRecordMapper.toDomain(it.data<DailyRecordDto>()) }
    }

    override fun observeRosterFinancials(rosterId: String): Flow<RosterFinancials?> =
        firestore.collection("roster_financials").document(rosterId).snapshots
            .map { snapshot -> if (snapshot.exists) rosterFinancialsMapper.toDomain(snapshot.data<RosterFinancialsDto>()) else null }
            // SH-188: refused under the rules (no money here any more): a typed error, not silence.
            .catch { e -> if (e.isPermissionDenied()) throw WorkplaceAccessRevokedException(e) else throw e }
            .distinctUntilChanged()

    override fun observeDailyRecord(dailyRecordId: String): Flow<DailyRecord> {
        return firestore.collection("daily_records")
            .document(dailyRecordId).snapshots.map { snapshot ->
            if (snapshot.exists) {
                val dto = snapshot.data<DailyRecordDto>()
                dailyRecordMapper.toDomain(dto)
            } else {
                throw Exception("Daily record document does not exist yet")
            }
            }.catch { e ->
                if (!e.isPermissionDenied()) {
                    throw e
                }
        }.distinctUntilChanged()
    }

    override suspend fun saveDailyRecord(record: DailyRecord): Result<Unit> {
        return try {
            val dto = dailyRecordMapper.toDto(record)
            firestore.collection("daily_records").document(record.id).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun saveDailyRecords(records: List<DailyRecord>): Result<Unit> = runCatching {
        // Firestore batches take up to 500 writes; a month is at most 31.
        records.chunked(450).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { record ->
                batch.set(firestore.collection("daily_records").document(record.id), dailyRecordMapper.toDto(record))
            }
            batch.commit()
        }
    }

    override suspend fun updateDailyRevenue(
        dailyRecordId: String,
        realNetto: Double?,
        planNetto: Double?,
        comments: String?
    ): Result<Unit> {
        return try {
            // Only the given fields change; nothing given, nothing written.
            if (realNetto != null || planNetto != null || comments != null) {
                firestore.collection("daily_records").document(dailyRecordId).updateFields {
                    realNetto?.let { "revenue.realNetto" to it }
                    planNetto?.let { "revenue.planNetto" to it }
                    comments?.let { "revenue.comments" to it }
                }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getShiftFinancials(shiftId: String): Result<ShiftFinancials> {
        return try {
            val snapshot = firestore.collection("shift_financials").document(shiftId).get()
            if (snapshot.exists) {
                val dto = snapshot.data<ShiftFinancialsDto>()
                Result.success(shiftFinancialsMapper.toDomain(dto))
            } else {
                Result.failure(Exception("Shift Financials not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getShiftFinancialsForRoster(rosterId: String): Result<List<ShiftFinancials>> {
        return try {
            val snapshot = firestore.collection("shift_financials")
                .where { "rosterId" equalTo rosterId }
                .get()

            val financials = snapshot.documents.map {
                shiftFinancialsMapper.toDomain(it.data<ShiftFinancialsDto>())
            }
            Result.success(financials)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getShiftFinancialsForUser(
        rosterId: String,
        userId: String
    ): Result<List<ShiftFinancials>> {
        return try {
            val snapshot = firestore.collection("shift_financials")
                .where { "rosterId" equalTo rosterId }
                .where { "userId" equalTo userId }
                .get()

            val financials = snapshot.documents.map {
                shiftFinancialsMapper.toDomain(it.data<ShiftFinancialsDto>())
            }
            Result.success(financials)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // --- WRITE OPERATIONS (Zoptymalizowane) ---

    override suspend fun updateShiftFinancials(financials: ShiftFinancials): Result<Unit> {
        return try {
            val dto = shiftFinancialsMapper.toDto(financials)
            firestore.collection("shift_financials").document(financials.shiftId).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteShiftFinancials(shiftId: String): Result<Unit> {
        return try {
            firestore.collection("shift_financials").document(shiftId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun writeShiftFinancials(save: List<ShiftFinancials>, delete: Collection<String>): Result<Unit> = runCatching {
        // SH-260: one batch per MAX_WRITES. The rules read the same few documents for every
        // snapshot of a roster (the caller's profile, the roster, the plan), so a batch of one
        // month stays within their 20 document reads (firebase/tests shift_financials batch).
        val collection = firestore.collection("shift_financials")
        val saved = save.map { it.shiftId }.toSet()
        // A delete is the shift id alone; a snapshot written for the same shift wins.
        val writes: List<Pair<String, ShiftFinancials?>> =
            delete.distinct().filter { it !in saved }.map { it to null } + save.map { it.shiftId to it }
        writes.chunked(ShiftBatches.MAX_WRITES).forEach { chunk ->
            val batch = firestore.batch()
            chunk.forEach { (shiftId, financials) ->
                if (financials == null) batch.delete(collection.document(shiftId))
                else batch.set(collection.document(shiftId), shiftFinancialsMapper.toDto(financials))
            }
            batch.commit()
        }
    }

    override suspend fun getAdjustmentsForUser(
        rosterId: String,
        userId: String
    ): Result<List<Adjustment>> = runCatching {
        // Related shifts are not read here: 20 adjustments would cost 21 reads. Screens look them
        // up in the shifts they already have.
        firestore.collection("adjustments")
            .where { "rosterId" equalTo rosterId }
            .where { "userId" equalTo userId }
            .get()
            .documents.map { adjustmentMapper.toDomain(it.data<AdjustmentDto>().copy(id = it.id)) }
    }

    override suspend fun getAdjustmentsForRoster(rosterId: String): Result<List<Adjustment>> = runCatching {
        firestore.collection("adjustments")
            .where { "rosterId" equalTo rosterId }
            .get()
            .documents.map { adjustmentMapper.toDomain(it.data<AdjustmentDto>().copy(id = it.id)) }
    }

    override suspend fun getAdjustment(adjustmentId: String): Result<Adjustment> = runCatching {
        val snapshot = firestore.collection("adjustments").document(adjustmentId).get()
        if (!snapshot.exists) throw NotFoundException("Adjustment $adjustmentId")
        adjustmentMapper.toDomain(snapshot.data<AdjustmentDto>().copy(id = snapshot.id))
    }

    override suspend fun saveAdjustment(adjustment: Adjustment): Result<Adjustment> = runCatching {
        val adjustments = firestore.collection("adjustments")
        // A new one gets its document id first, so the stored document carries it (edits and deletes need it).
        val ref = if (adjustment.id.isBlank()) adjustments.document else adjustments.document(adjustment.id)
        val stored = adjustment.copy(id = ref.id)
        ref.set(adjustmentMapper.toDto(stored))
        stored
    }

    override suspend fun deleteAdjustment(adjustmentId: String): Result<Unit> = runCatching {
        firestore.collection("adjustments").document(adjustmentId).delete()
    }

    override suspend fun lockDailyRecord(dailyRecordId: String): Result<Unit> {
        return try {
            firestore.collection("daily_records").document(dailyRecordId).updateFields { "isLocked" to true }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun unlockDailyRecord(dailyRecordId: String): Result<Unit> = runCatching {
        firestore.collection("daily_records").document(dailyRecordId).updateFields { "isLocked" to false }
    }

    override suspend fun getFixedCosts(rosterId: String): Result<List<FixedCost>> {
        return try {
            val snapshot =
                firestore.collection("rosters").document(rosterId).collection("costs").get()
            val costs = snapshot.documents.map { costMapper.toDomain(it.data<CostDto>()) }
            Result.success(costs)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun saveFixedCost(rosterId: String, cost: FixedCost): Result<FixedCost> = runCatching {
        val costs = firestore.collection("rosters").document(rosterId).collection("costs")
        val ref = if (cost.id.isBlank()) costs.document else costs.document(cost.id)
        val stored = cost.copy(id = ref.id)
        ref.set(costMapper.toDto(stored))
        stored
    }

    override suspend fun deleteFixedCost(rosterId: String, costId: String): Result<Unit> = runCatching {
        firestore.collection("rosters").document(rosterId).collection("costs").document(costId).delete()
    }
}